import {Component, ElementRef, Inject, OnInit, inject, DestroyRef, signal, computed} from '@angular/core';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {CommonModule} from '@angular/common';
import {DialogRestService} from "../../../core/cms/services/dialog.rest.service";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DialogTextModel} from "../../../core/cms/models/dialog.text.model";
import {MatButtonModule} from "@angular/material/button";
import {finalize, take} from "rxjs/operators";
import {LogService} from "../../../core/base/log.service";


interface CustomUpdateItem {
  content: string;
  timer: number;
}

const PROGRESS_INCREMENT = 1;
const TIMER_INTERVAL = 100; // ms

@Component({
  selector: 'app-update',
  standalone: true,
  imports: [
    MatProgressBarModule,
    MatDialogModule,
    MatButtonModule,
    CommonModule
  ],
  templateUrl: './update.component.html',
  styleUrl: './update.component.scss'
})
export class UpdateComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<UpdateComponent>);
  private readonly dialogRestService = inject(DialogRestService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly log = inject(LogService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly progress = signal(0);
  protected readonly listItems = signal<string[]>([]);
  protected readonly isComplete = computed(() => this.progress() === 100);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  private customUpdateListArray: CustomUpdateItem[] = [];
  private updateListArray: string[] = [];
  private currentIndex = 0;
  private timer?: ReturnType<typeof setInterval>;
  private hasCustomTimer = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogTextModel) {
    this.dialogRef.disableClose = true;
    this.destroyRef.onDestroy(() => this.stopProgress());
    this.parseDialogContent();
  }

  ngOnInit(): void {
    this.startProgress();
  }

  private parseDialogContent(): void {
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.data.body, 'text/html');

    this.hasCustomTimer = !!doc.getElementById('customTimer');
    const liElements = Array.from(doc.getElementsByTagName('li'));

    liElements.forEach((li) => {
      if (li.id.includes('showAt')) {
        const timer = Number(li.id.split('showAt-')[1]);
        this.customUpdateListArray.push({
          content: li.innerHTML,
          timer
        });
      } else {
        this.updateListArray.push(li.innerHTML);
      }
    });

    this.customUpdateListArray.sort((a, b) => a.timer - b.timer);

    if (!this.hasCustomTimer) {
      this.listItems.set(this.updateListArray);
    }
  }

  private startProgress(): void {
    this.timer = setInterval(() => {
      if (this.hasCustomTimer) {
        this.manageProgressWithCustomTimer();
      } else {
        this.manageStandardProgress();
      }
    }, TIMER_INTERVAL);
  }

  private manageProgressWithCustomTimer(): void {
    const currentProgress = this.progress();

    if (currentProgress === 100) {
      this.completeProgress();
      return;
    }

    const nextItem = this.customUpdateListArray[this.currentIndex];
    if (nextItem && currentProgress === nextItem.timer) {
      this.listItems.update(items => [...items, nextItem.content]);
      this.currentIndex++;
    }

    this.progress.update(p => p + PROGRESS_INCREMENT);
  }

  private manageStandardProgress(): void {
    if (this.progress() === 100) {
      this.completeProgress();
      return;
    }

    this.progress.update(p => p + PROGRESS_INCREMENT);
  }

  private completeProgress(): void {
    this.stopProgress();
    this.elementRef.nativeElement.querySelectorAll('.disabled').forEach(element => {
      element.classList.remove('disabled');
    });
  }

  private stopProgress(): void {
    if (this.timer !== undefined) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  protected onSubscribe(action: 'update' | 'show video'): void {
    if (!this.isComplete() || this.saving()) return;
    if (action === 'show video') {
      window.open('/#/static-category?url=%2Fhilfe-meinebelege', '_self');
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    this.dialogRestService
      .subscribeToDialogOffer(this.data.id, this.data.idf)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef), finalize(() => this.saving.set(false)))
      .subscribe({
        next: res => {
          if (res.return_object === null || res.error_code !== null) {
            this.error.set('Die Bestätigung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
            this.log.error('Unable to confirm update popup', res.error_code);
            return;
          }
          if (this.data.link) {
            window.open(this.data.link, this.data.target);
          }
          this.dialogRef.close();
        },
        error: error => {
          this.error.set('Die Bestätigung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
          this.log.error('Unable to confirm update popup', error);
        }
      });
  }
}
